import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { FaRecycle, FaFlask, FaShieldAlt, FaCheckCircle, FaTools, FaChartLine, FaCog, FaWater, FaIndustry } from 'react-icons/fa';

const EffluentTreatment = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={8}>
              <h1 className="display-4 fw-bold mb-4">Effluent Treatment (ETP)</h1>
              <p className="lead mb-4">
                Comprehensive effluent treatment plant solutions for industrial wastewater management, 
                ensuring regulatory compliance and environmental protection.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Overview */}
      <section className="section-padding">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Effluent Treatment Importance</h2>
              <p className="lead">
                Industrial effluent treatment is essential for environmental protection, regulatory compliance, 
                and sustainable water management in industrial operations.
              </p>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col lg={6}>
              <h4 className="mb-3">Environmental Compliance:</h4>
              <ul>
                <li>Meet regulatory discharge standards</li>
                <li>Protect water bodies and ecosystems</li>
                <li>Prevent environmental pollution</li>
                <li>Ensure sustainable operations</li>
                <li>Avoid regulatory penalties</li>
              </ul>
            </Col>
            <Col lg={6}>
              <h4 className="mb-3">Treatment Benefits:</h4>
              <ul>
                <li>Water recycling and reuse</li>
                <li>Reduced environmental impact</li>
                <li>Resource recovery opportunities</li>
                <li>Enhanced corporate responsibility</li>
                <li>Cost-effective wastewater management</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Treatment Stages */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">ETP Treatment Stages</h2>
              <p className="lead">Multi-stage treatment process for comprehensive effluent management</p>
            </Col>
          </Row>
          
          <Row>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaCog /></div>
                  <Card.Title>Primary Treatment</Card.Title>
                  <Card.Text>
                    Physical processes for removal of suspended solids and large particles.
                  </Card.Text>
                  <Badge bg="primary">Essential</Badge>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaFlask /></div>
                  <Card.Title>Secondary Treatment</Card.Title>
                  <Card.Text>
                    Biological processes for organic matter removal and BOD/COD reduction.
                  </Card.Text>
                  <Badge bg="success">Biological</Badge>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4} className="mb-4">
              <Card className="h-100">
                <Card.Body className="text-center">
                  <div className="feature-icon mb-3"><FaWater /></div>
                  <Card.Title>Tertiary Treatment</Card.Title>
                  <Card.Text>
                    Advanced processes for polishing and meeting stringent discharge standards.
                  </Card.Text>
                  <Badge bg="info">Advanced</Badge>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Treatment Technologies */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Advanced Treatment Technologies</h2>
              <p className="lead">State-of-the-art solutions for efficient effluent treatment</p>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaIndustry /></div>
                <h5>Physico-Chemical</h5>
                <p className="small">Coagulation, flocculation, and sedimentation processes</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaRecycle /></div>
                <h5>Biological</h5>
                <p className="small">Aerobic, anaerobic, and activated sludge processes</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaShieldAlt /></div>
                <h5>Membrane</h5>
                <p className="small">Ultrafiltration, nanofiltration, and reverse osmosis systems</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaFlask /></div>
                <h5>Advanced Oxidation</h5>
                <p className="small">Ozonation, UV treatment, and advanced oxidation processes</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Treatment Processes */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Treatment Process Details</h2>
              <p className="lead">Comprehensive treatment approach for various effluent types</p>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaCog className="me-2" />Primary Treatment</h4>
              <ul>
                <li>Screening and grit removal</li>
                <li>Equalization and neutralization</li>
                <li>Sedimentation and clarification</li>
                <li>Oil and grease separation</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaRecycle className="me-2" />Secondary Treatment</h4>
              <ul>
                <li>Activated sludge process</li>
                <li>Trickling filter systems</li>
                <li>Sequencing batch reactors</li>
                <li>Biological nutrient removal</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaWater className="me-2" />Tertiary Treatment</h4>
              <ul>
                <li>Membrane filtration systems</li>
                <li>Advanced oxidation processes</li>
                <li>Disinfection and pathogen removal</li>
                <li>Sludge treatment and disposal</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaShieldAlt className="me-2" />Sludge Management</h4>
              <ul>
                <li>Sludge thickening and dewatering</li>
                <li>Anaerobic digestion</li>
                <li>Composting and resource recovery</li>
                <li>Safe disposal and utilization</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Industry Applications */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Industry-Specific Solutions</h2>
              <p className="lead">Customized ETP solutions for various industrial sectors</p>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaIndustry /></div>
                <h5>Textile Industry</h5>
                <p className="small">Color removal, BOD/COD reduction, and heavy metal removal</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaFlask /></div>
                <h5>Pharmaceutical</h5>
                <p className="small">Advanced oxidation, membrane filtration, and disinfection</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaCog /></div>
                <h5>Chemical Industry</h5>
                <p className="small">Toxic compound removal, pH adjustment, and neutralization</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaWater /></div>
                <h5>Food & Beverage</h5>
                <p className="small">Organic load reduction, nutrient removal, and water reuse</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Treatment Chemicals */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">ETP Treatment Chemicals</h2>
              <p className="lead">Comprehensive chemical portfolio for effluent treatment</p>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaFlask className="me-2" />Coagulants & Flocculants</h4>
              <ul>
                <li>Alum, ferric chloride, and polymeric coagulants</li>
                <li>Anionic and cationic flocculants</li>
                <li>Organic and inorganic polymers</li>
                <li>Custom formulations for specific effluents</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaShieldAlt className="me-2" />pH Control Chemicals</h4>
              <ul>
                <li>Acids (sulfuric, hydrochloric, citric)</li>
                <li>Alkalis (caustic soda, lime, sodium carbonate)</li>
                <li>Buffering agents</li>
                <li>pH stabilizers and neutralizers</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaRecycle className="me-2" />Biological Treatment</h4>
              <ul>
                <li>Enzymes and bio-catalysts</li>
                <li>Nutrient supplements</li>
                <li>Biological oxygen donors</li>
                <li>Microbial cultures and starters</li>
              </ul>
            </Col>
            <Col lg={6} className="mb-4">
              <h4 className="mb-3"><FaCog className="me-2" />Specialty Chemicals</h4>
              <ul>
                <li>Deodorizing agents</li>
                <li>Heavy metal precipitants</li>
                <li>Color removal agents</li>
                <li>Disinfectants and sanitizers</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Monitoring & Control */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold">Monitoring & Control Systems</h2>
              <p className="lead">Advanced monitoring for optimal ETP performance</p>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaChartLine /></div>
                <h5>Online Monitoring</h5>
                <p className="small">Real-time parameter tracking with automated controls</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaTools /></div>
                <h5>Automated Dosing</h5>
                <p className="small">Precise chemical dosing with feedback control systems</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaCheckCircle /></div>
                <h5>Effluent Analysis</h5>
                <p className="small">Comprehensive laboratory testing and analysis services</p>
              </div>
            </Col>
            <Col md={3} className="text-center mb-4">
              <div className="d-flex flex-column align-items-center">
                <div className="feature-icon mb-3"><FaCog /></div>
                <h5>Compliance Reporting</h5>
                <p className="small">Regulatory compliance reports and documentation</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Technical Specifications */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Technical Specifications</h2>
              <p className="lead">ETP design parameters and performance standards</p>
            </Col>
          </Row>
          
          <Row>
            <Col lg={6}>
              <h4 className="mb-3">Effluent Quality Parameters:</h4>
              <ul>
                <li>pH: 6.0-9.0 (discharge standard)</li>
                <li>BOD: &lt;30 mg/L (as per standards)</li>
                <li>COD: &lt;250 mg/L (industry dependent)</li>
                <li>TSS: &lt;100 mg/L</li>
                <li>Oil & Grease: &lt;10 mg/L</li>
              </ul>
            </Col>
            <Col lg={6}>
              <h4 className="mb-3">ETP Equipment:</h4>
              <ul>
                <li>Collection and equalization tanks</li>
                <li>Primary and secondary clarifiers</li>
                <li>Aeration systems and bioreactors</li>
                <li>Membrane filtration units</li>
                <li>Sludge handling and disposal systems</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-primary text-white">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 fw-bold mb-4">Need Effluent Treatment Solutions?</h2>
              <p className="lead mb-4">
                Contact us for comprehensive ETP design, installation, and operational support.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Button variant="warning" size="lg" className="cta-button">
                  ETP Design Consultation
                </Button>
                <Button variant="outline-light" size="lg">
                  Compliance Assessment
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default EffluentTreatment;
